import { Flex } from '@chakra-ui/react';
import { ReactComponent as CheckIcon } from 'assets/icons/check_circle_outline.svg';
import React, { ReactNode, memo, useMemo } from 'react';

type ChoiceButtonProps = {
  children: ReactNode;
  isReadOnly: boolean;
  isSelected: boolean;
  isCorrect: boolean;
  showResult: boolean;
  onClick: React.MouseEventHandler<HTMLDivElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLDivElement>;
};

/**
 * Render the question section on the side panel settings
 */
export const ChoiceButton = memo(
  ({ children, isReadOnly, isSelected, isCorrect, showResult, onClick, onKeyDown }: ChoiceButtonProps) => {
    const componentStyle = useMemo(() => {
      if (showResult) {
        return {
          borderColor: !isSelected ? 'green.500' : 'red.600',
          outlineColor: isCorrect || !isSelected ? 'green.500' : 'red.600',
          bg: isCorrect || !isSelected ? 'white' : 'red.50',
        };
      }
      return {
        borderColor: isSelected ? 'upgrade.blue.500' : 'outline.gray',
        outlineColor: isSelected ? 'upgrade.blue.500' : 'unset',
        bg: isSelected ? 'upgrade.blue.50' : 'white',
      };
    }, [isCorrect, isSelected, showResult]);

    return (
      <Flex
        as={isReadOnly ? 'button' : undefined}
        position='relative'
        px={'30px'}
        py={'7px'}
        borderWidth={'1px'}
        borderRadius={'5px'}
        outline={isSelected ? 'solid 2px' : 'unset'}
        outlineOffset={'-2px'}
        flex='1'
        w='full'
        textAlign={'left'}
        _hover={{
          boxShadow: '0px 0px 0px 2px rgba(0, 0, 0)',
        }}
        _focus={{
          boxShadow: '0px 0px 0px 2px rgba(0, 0, 0)',
        }}
        onMouseDown={onClick}
        onKeyDown={onKeyDown}
        {...componentStyle}
      >
        {children}
        {(isCorrect || !isSelected) && (
          <Flex position='absolute' w='full' h='full' alignItems='center' top={0} left={2} hidden={!showResult}>
            <CheckIcon width={'18px'} height='18px' color='var(--vg-colors-action-green-500)' />
          </Flex>
        )}
      </Flex>
    );
  },
);

ChoiceButton.displayName = 'Choice Button';

export default ChoiceButton;
